import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Factory, Plus, ArrowsClockwise, PencilSimple, CircleNotch, ClockCounterClockwise, CheckCircle, WarningCircle, DownloadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Toaster, toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { MachineImportDialog, SapMachineBanner } from "@/components/MachineImportDialog";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SYNC_LABELS = {
  synced: { label: "In SAP", className: "bg-[#ECFDF3] text-[#027A48] border-[#A6F4C5]" },
  not_in_sap: { label: "Not in SAP", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  awaiting_sap_service: { label: "Awaiting SAP service", className: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" },
  failed: { label: "SAP link failed", className: "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]" },
  not_synced: { label: "Not checked", className: "bg-[#F2F4F7] text-[#475467] border-[#E4E7EC]" },
};

const MachineDialog = ({ open, machine, config, sapResources, onClose, onSaved }) => {
  const editing = !!machine;
  const [form, setForm] = useState({ machine_id: "", name: "", site_id: "", work_center: "", description: "", sap_resource_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(machine
      ? { machine_id: machine.machine_id, name: machine.name || "", site_id: machine.site_id || "", work_center: machine.work_center || "", description: machine.description || "", sap_resource_id: machine.sap_resource_id || "" }
      : { machine_id: "", name: "", site_id: "", work_center: "", description: "", sap_resource_id: "" });
  }, [open, machine]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        const { data } = await axios.patch(`${API}/machines/${encodeURIComponent(form.machine_id)}`, {
          name: form.name, site_id: form.site_id, work_center: form.work_center,
          description: form.description, sap_resource_id: form.sap_resource_id === "__none__" ? "" : form.sap_resource_id,
        });
        toast.success(`${data.machine_id} updated`);
      } else {
        const { data } = await axios.post(`${API}/machines`, {
          ...form, sap_resource_id: form.sap_resource_id === "__none__" ? "" : form.sap_resource_id,
        });
        toast.success(`${data.machine_id} created`);
        if (data.sap_sync_status !== "synced") toast.warning(data.sap_sync_note || "Machine saved here; not linked to a SAP resource yet.");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the machine");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[520px]" data-testid="machine-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{editing ? `Edit ${machine.machine_id}` : "New Machine"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold text-[#344054]">Machine ID</Label>
            <Input
              value={form.machine_id} disabled={editing}
              onChange={(e) => setForm({ ...form, machine_id: e.target.value.toUpperCase() })}
              placeholder="PRESS-01" className="font-mono" data-testid="machine-id-input"
            />
            {!editing && <p className="text-[11px] text-[#98A2B3] mt-1">Upper case, 2-20 characters. This is what the shop floor app shows and what SAP will use as the Resource ID.</p>}
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="200T Power Press" data-testid="machine-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="machine-site-select"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>
                  {(config.sites || []).map((s) => <SelectItem key={s} value={s} data-testid={`machine-site-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Work Center</Label>
              <Select value={form.work_center} onValueChange={(v) => setForm({ ...form, work_center: v })}>
                <SelectTrigger data-testid="machine-work-center-select"><SelectValue placeholder="Select work center" /></SelectTrigger>
                <SelectContent>
                  {(config.work_centers || []).map((w) => <SelectItem key={w} value={w} data-testid={`machine-work-center-option-${w}`}>{w}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Description (optional)</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="machine-description-input" />
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">SAP Resource (optional)</Label>
            <Select value={form.sap_resource_id || "__none__"} onValueChange={(v) => setForm({ ...form, sap_resource_id: v })}>
              <SelectTrigger data-testid="machine-sap-resource-select"><SelectValue placeholder="Link to an existing SAP resource" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" data-testid="machine-sap-resource-option-none">— none —</SelectItem>
                {(sapResources || []).map((r) => (
                  <SelectItem key={r.resource_id} value={r.resource_id} data-testid={`machine-sap-resource-option-${r.resource_id}`}>
                    {r.resource_id}{r.description ? ` · ${r.description}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#98A2B3] mt-1">Pick the resource SAP already has for this machine. Leave it empty and we look for one with the same ID.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="machine-dialog-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || !form.machine_id || !form.name || !form.site_id || !form.work_center}
            className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="machine-dialog-save">
            {saving ? <CircleNotch size={14} className="animate-spin" /> : editing ? "Save changes" : "Create machine"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function MachineMasterPage() {
  const [machines, setMachines] = useState([]);
  const [config, setConfig] = useState({ sites: [], work_centers: [], sap_resource_service_configured: false });
  const [sapResources, setSapResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [audit, setAudit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pendingInSap, setPendingInSap] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [machineRes, configRes] = await Promise.all([
        axios.get(`${API}/machines`),
        axios.get(`${API}/machines/config`),
      ]);
      setMachines(machineRes.data.machines || []);
      setConfig(configRes.data || {});
      if (configRes.data?.sap_resource_service_configured) {
        axios.get(`${API}/machines/sap-resources`)
          .then(({ data }) => setSapResources(data.resources || []))
          .catch(() => setSapResources([]));
        // Nov 3 2026: "N machines exist in SAP but not here" - the one-click
        // import the user asked for, alongside the nightly auto-import.
        axios.get(`${API}/machines/sap-import-preview`)
          .then(({ data }) => setPendingInSap(data.pending_count || 0))
          .catch(() => setPendingInSap(0));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the machine master");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (machine) => {
    try {
      await axios.patch(`${API}/machines/${encodeURIComponent(machine.machine_id)}`, { active: !machine.active });
      toast.success(`${machine.machine_id} ${machine.active ? "deactivated" : "activated"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the machine");
    }
  };

  const pushToSap = async (machine) => {
    setSyncingId(machine.machine_id);
    try {
      const { data } = await axios.post(`${API}/machines/${encodeURIComponent(machine.machine_id)}/sap-sync`);
      if (data.sap_sync_status === "synced") toast.success(`${data.machine_id} linked to SAP resource ${data.sap_resource_id}`);
      else toast.warning(data.sap_sync_note || "SAP has no matching resource yet");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "SAP check failed");
    } finally {
      setSyncingId(null);
    }
  };

  const openAudit = async (machine) => {
    try {
      const { data } = await axios.get(`${API}/machines/audit`, { params: { machine_id: machine.machine_id } });
      setAudit({ machine, rows: data.audit || [] });
    } catch (e) {
      toast.error("Could not load the audit trail");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="machine-master-page">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Factory size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Machine Master</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0"><NavTabs /></div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-4 sm:p-6 max-w-6xl w-full mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Factory size={20} weight="fill" className="text-[#0E7C86]" />
              <h1 className="font-heading text-lg font-bold text-[#101828]">Machine Master</h1>
            </div>
            <p className="text-sm text-[#667085] max-w-2xl">
              Every machine the Shop Floor app can be opened on. Each one belongs to one site and one work center -
              the operator picks a machine first, then only sees parts with lots running at that site.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {config.sap_resource_service_configured && (
              <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="machine-import-btn">
                <DownloadSimple size={14} weight="bold" className="mr-1.5" /> Import from SAP
              </Button>
            )}
            <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-[#0E7C86] hover:bg-[#0B6B74]" data-testid="machine-add-btn">
              <Plus size={14} weight="bold" className="mr-1.5" /> New Machine
            </Button>
          </div>
        </div>

        <SapMachineBanner pending={pendingInSap} onImport={() => setImportOpen(true)} />

        {!config.sap_resource_service_configured && (
          <div className="flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-lg px-3 py-2.5 mb-5 text-[12px] text-[#B54708]" data-testid="machine-sap-service-warning">
            <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
            <span>
              No SAP Resource service is reachable from here yet, so machines cannot be linked to their SAP equipment
              resource. Ask the key user to expose the Equipment Resource business object through Application and User
              Management → OData Services. Nothing on the shop floor depends on it - IDs stay the same when it is switched on.
            </span>
          </div>
        )}

        {config.sap_resource_service_configured && (
          <div className="flex items-start gap-2 bg-[#F0F9FF] border border-[#B9E6FE] rounded-lg px-3 py-2.5 mb-5 text-[12px] text-[#026AA2]" data-testid="machine-sap-service-note">
            <WarningCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
            <span>
              Machines link to the equipment resource SAP already has — pick it in the machine dialog
              ({sapResources.length} available), or give the machine the same ID and press Sync. SAP has
              <strong> no API that creates a resource</strong>: the only released resource service is read-only, so a
              brand-new resource is added once in the SAP UI (Planning and Production Master Data → Resources → New
              Equipment Resource) and links itself here on the next Sync. Nothing on the floor waits for that — hover a
              machine's SAP badge for its exact status.
            </span>
          </div>
        )}

        <div className="bg-white border border-[#E4E7EC] rounded-xl overflow-x-auto">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead className="text-xs">Machine</TableHead>
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Site</TableHead>
                <TableHead className="text-xs">Work Center</TableHead>
                <TableHead className="text-xs">SAP</TableHead>
                <TableHead className="text-xs">Active</TableHead>
                <TableHead className="text-xs w-28"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-sm text-[#667085] py-6 text-center" data-testid="machine-loading">Loading machines...</TableCell></TableRow>}
              {!loading && machines.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-sm text-[#667085] py-6 text-center" data-testid="machine-empty">No machines yet - add the first one so the shop floor app has something to open.</TableCell></TableRow>
              )}
              {machines.map((m) => {
                const sync = SYNC_LABELS[m.sap_sync_status] || SYNC_LABELS.not_synced;
                return (
                  <TableRow key={m.machine_id} data-testid={`machine-row-${m.machine_id}`}>
                    <TableCell className="font-mono text-sm font-bold text-[#101828]">{m.machine_id}</TableCell>
                    <TableCell className="text-sm text-[#344054]">{m.name}</TableCell>
                    <TableCell className="text-sm text-[#344054]">{m.site_id}</TableCell>
                    <TableCell className="text-sm text-[#344054]">{m.work_center}</TableCell>
                    <TableCell>
                      <Badge variant="outline" title={m.sap_sync_note || ""} className={`text-[10px] ${sync.className}`} data-testid={`machine-sync-${m.machine_id}`}>
                        {m.sap_sync_status === "synced" && <CheckCircle size={11} weight="fill" className="mr-1" />}
                        {sync.label}{m.sap_resource_id ? ` · ${m.sap_resource_id}` : ""}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={m.active} onCheckedChange={() => toggleActive(m)} data-testid={`machine-active-toggle-${m.machine_id}`} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => { setEditing(m); setDialogOpen(true); }} className="text-[#0E7C86]" title="Edit" data-testid={`machine-edit-${m.machine_id}`}>
                          <PencilSimple size={15} weight="bold" />
                        </button>
                        <button onClick={() => pushToSap(m)} disabled={syncingId === m.machine_id} className="text-[#475467] disabled:opacity-40" title="Re-check SAP and link" data-testid={`machine-sync-btn-${m.machine_id}`}>
                          {syncingId === m.machine_id ? <CircleNotch size={15} className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
                        </button>
                        <button onClick={() => openAudit(m)} className="text-[#475467]" title="Audit trail" data-testid={`machine-audit-${m.machine_id}`}>
                          <ClockCounterClockwise size={15} weight="bold" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <p className="text-[12px] text-[#475467] mt-3">
          Shop Floor app API documentation (no login needed):{" "}
          <a href="/shopfloor-api.html" target="_blank" rel="noreferrer" className="text-[#0E7C86] font-bold underline" data-testid="machine-api-docs-link">/shopfloor-api.html</a>
        </p>
      </div>

      <Dialog open={!!audit} onOpenChange={(v) => !v && setAudit(null)}>
        <DialogContent className="sm:max-w-[640px]" data-testid="machine-audit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-base">{audit?.machine.machine_id} · Audit Trail</DialogTitle></DialogHeader>
          <div className="max-h-[420px] overflow-auto">
            {(audit?.rows || []).length === 0 && <p className="text-sm text-[#667085]">Nothing recorded yet.</p>}
            {(audit?.rows || []).map((r, i) => (
              <div key={i} className="border-b border-[#F2F4F7] py-2 text-xs" data-testid={`machine-audit-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-bold text-[#101828]">{r.action}</span>
                  <span className="text-[#98A2B3]">{r.at ? new Date(r.at).toLocaleString() : ""}</span>
                </div>
                <div className="text-[#667085]">by {r.actor}{r.note ? ` · ${r.note}` : ""}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <MachineDialog open={dialogOpen} machine={editing} config={config} sapResources={sapResources} onClose={() => setDialogOpen(false)} onSaved={load} />
      <MachineImportDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
    </div>
  );
}
